import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { shipmentMissingService } from "../../services/shipmentMissingService"
import { shipmentService } from "../../services/shipmentService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { PageLayout, Button } from "../common/index.js";

const LABEL_CLS = "block text-xs font-medium text-secondary mb-1"
const INPUT_CLS =
  "w-full px-3 py-1.5 text-sm border border-gray-300 rounded-input focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
const CARD_CLS = "bg-white rounded-card shadow-card p-3 sm:p-4 mb-4"

const AddMissing = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const [shipmentDetails, setShipmentDetails] = useState(null)
  const [formData, setFormData] = useState({
    shipment_id: "",
    reason: "",
    missing_details: "",
    quantity_missing: "",
  })

  const handleShipmentSearch = async (e) => {
    const shipmentId = e.target.value
    setFormData({ ...formData, shipment_id: shipmentId })
    setShipmentDetails(null)

    if (shipmentId.trim().length === 0) {
      return
    }

    try {
      setSearchLoading(true)
      const response = await shipmentService.getShipment(shipmentId)

      const shipment = response.data.shipment || response.data.data

      if (shipment) {
        setShipmentDetails(shipment)
      } else {
        setShipmentDetails(null)
        Alert.error("Shipment not found")
      }
    } catch (error) {
      setShipmentDetails(null)
      console.error("Shipment search error:", error)

      if (error.response?.status === 404) {
        Alert.error("Shipment not found")
      } else if (error.response?.data?.error) {
        Alert.error(error.response.data.error)
      } else {
        Alert.error("Error searching for shipment. Please try again.")
      }
    } finally {
      setSearchLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
  }

  const validateForm = () => {
    if (!formData.shipment_id.trim()) {
      Alert.error("Shipment ID is required")
      return false
    }

    if (!shipmentDetails) {
      Alert.error("Please search and select a valid shipment")
      return false
    }

    if (!formData.reason.trim()) {
      Alert.error("Reason is required")
      return false
    }

    if (formData.reason.trim().length < 10) {
      Alert.error("Please provide a detailed reason (at least 10 characters)")
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const confirmed = await Alert.confirm(
      `Report missing items for shipment ${formData.shipment_id}?`,
      "Confirm Missing Report",
    )

    if (!confirmed) return

    try {
      setLoading(true)

      const submitData = {
        shipment_id: formData.shipment_id.trim(),
        reason: formData.reason.trim(),
        missing_details: formData.missing_details.trim(),
        quantity_missing: formData.quantity_missing ? Number.parseInt(formData.quantity_missing) : null,
      }

      await shipmentMissingService.createMissing(submitData)

      Alert.success("Missing shipment report created successfully!")
      navigate("/missing")
    } catch (error) {
      console.error("Error creating missing report:", error)

      if (error.response?.data?.error) {
        Alert.error(error.response.data.error)
      } else {
        Alert.error("Failed to create missing report. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageLayout title="Report Missing Shipment" icon={AlertTriangle} maxWidth="max-w-7xl">
      <Button
        variant="ghost"
        size="sm"
        icon={ArrowLeft}
        onClick={() => navigate("/missing")}
        className="mb-4 -ml-2"
      >
        Back to Missing Shipments
      </Button>
                {/* Shipment Lookup Card */}
                <div className={CARD_CLS}>
                  <h2 className="text-sm font-semibold text-secondary mb-2 pb-2 border-b border-gray-200">Shipment Selection</h2>
                  <div className="mb-4">
                    <label className={LABEL_CLS}>
                      Shipment ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="shipment_id"
                      value={formData.shipment_id}
                      onChange={handleShipmentSearch}
                      placeholder="Enter shipment ID to search (e.g., 123456)"
                      className={`${INPUT_CLS} font-mono`}
                      required
                      disabled={loading}
                    />
                    {searchLoading && (
                      <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
                        <div className="animate-spin rounded-full h-4 w-4 border border-red-600 border-t-transparent"></div>
                        <span>Searching for shipment...</span>
                      </div>
                    )}
                  </div>

                  {/* Shipment Details Display */}
                  {shipmentDetails && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 p-4 sm:p-6 rounded-xl">
                      <div className="flex items-center gap-2 mb-4">
                        <h3 className="text-lg font-bold text-green-800">Shipment Found</h3>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 bg-white p-4 sm:p-5 rounded-xl border border-gray-200">
                        <div>
                          <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Shipment ID</p>
                          <p className="text-base sm:text-sm font-semibold text-secondary">
                            #{shipmentDetails.shipment_id}
                          </p>
                        </div>
                        <div>
                          <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Status</p>
                          <span className="text-base sm:text-sm font-semibold text-secondary">
                            {shipmentDetails.status}
                          </span>
                        </div>
                        <div>
                          <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Sender Name</p>
                          <p className="text-base sm:text-sm font-semibold text-secondary">{shipmentDetails.sender_name}</p>
                        </div>
                        <div>
                          <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Receiver Name</p>
                          <p className="text-base sm:text-sm font-semibold text-secondary">{shipmentDetails.receiver_name}</p>
                        </div>
                        <div className="sm:col-span-2">
                          <p className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Delivery Address</p>
                          <p className="text-base sm:text-sm font-semibold text-secondary">{shipmentDetails.receiver_address}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Missing Report Form */}
                <form onSubmit={handleSubmit} className={`${CARD_CLS} mb-0`}>
                  <h3 className="text-sm font-semibold text-secondary mb-2 pb-2 border-b border-gray-200">
                    Missing Item Details
                  </h3>

                  <div className="space-y-4 pt-2">
                    <div>
                      <label className={LABEL_CLS}>
                        Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="reason"
                        value={formData.reason}
                        onChange={handleChange}
                        placeholder="Describe why items are missing or damaged (e.g., items not found during delivery, damaged in transit, quantity mismatch, etc.)"
                        rows="4"
                        className={`${INPUT_CLS} resize-none`}
                        required
                        disabled={loading}
                      />
                      <div className="flex items-center justify-between mt-2 text-xs">
                        <span
                          className={`font-semibold ${formData.reason.length < 10 ? "text-red-600" : "text-green-600"}`}
                        >
                          {formData.reason.length} / 10 characters minimum
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className={LABEL_CLS}>Detailed Description</label>
                      <textarea
                        name="missing_details"
                        value={formData.missing_details}
                        onChange={handleChange}
                        placeholder="Additional details about the missing or damaged items"
                        rows="3"
                        className={`${INPUT_CLS} resize-none`}
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className={LABEL_CLS}>Quantity Missing (Optional)</label>
                      <input
                        type="number"
                        name="quantity_missing"
                        value={formData.quantity_missing}
                        onChange={handleChange}
                        placeholder="Number of items missing"
                        min="0"
                        className={INPUT_CLS}
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="pt-4 mt-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || !shipmentDetails || formData.reason.trim().length < 10}
                      loading={loading}
                    >
                      {loading ? "Submitting Report..." : "Submit Missing Report"}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => navigate("/missing")} disabled={loading}>
                      Cancel
                    </Button>
                  </div>
                </form>
    </PageLayout>
  )
}

export default AddMissing;